import {
  MedicalCasePhaseDocumentState,
  MedicalCasePhases,
  MedicalCaseProcessingPhase,
} from '../../../domain/medical-case/model';
import {
  MedicalCasePipelineFlow,
  PHASE_TO_STEP_MAP,
  PreparationStepKey,
  WAYBACK_PHASE_TO_STEP_MAP,
} from '../model/pipeline-step.model';
import { MedicalCaseDocumentResolver } from './medical-case-document-resolver.util';
import { MedicalCaseStatusResolver, StepBadgeStatus } from './medical-case-status-resolver.util';

export { type StepPhaseStatusResult as StepBadgeStatus } from './medical-case-status-resolver.util';
export type StepPhaseStatusResult = StepBadgeStatus;

/**
 * @Applied: [PR1] - Delegating to canonical MedicalCaseStatusResolver.
 */
export class MedicalCasePhaseStatusUtil {
  static getStepStatusesFromPhases(
    phases: MedicalCasePhases | null | undefined,
    flow: MedicalCasePipelineFlow = 'preparation'
  ): Partial<Record<PreparationStepKey, StepBadgeStatus>> {
    if (phases == null) {
      return {};
    }

    const result: Partial<Record<PreparationStepKey, StepBadgeStatus>> = {};

    const phaseToStepMap =
      flow === 'wayback' ? WAYBACK_PHASE_TO_STEP_MAP : PHASE_TO_STEP_MAP;

    for (const phaseName of Object.keys(phases) as MedicalCaseProcessingPhase[]) {
      const step = phaseToStepMap[phaseName];
      if (step == null) {
        continue;
      }

      result[step] = MedicalCaseStatusResolver.getStepBadgeStatus(phases, step, flow);
    }

    return result;
  }

  static getDocumentStatusesForStep(
    phases: MedicalCasePhases | null | undefined,
    step: PreparationStepKey,
    flow: MedicalCasePipelineFlow = 'preparation'
  ): Map<string, MedicalCasePhaseDocumentState> {
    if (phases == null) {
      return new Map();
    }

    const phaseToStepMap =
      flow === 'wayback' ? WAYBACK_PHASE_TO_STEP_MAP : PHASE_TO_STEP_MAP;

    const result = new Map<string, MedicalCasePhaseDocumentState>();

    for (const [phaseName, phaseData] of Object.entries(phases) as [
      MedicalCaseProcessingPhase,
      MedicalCasePhases[MedicalCaseProcessingPhase],
    ][]) {
      if (
        phaseData == null ||
        phaseToStepMap[phaseName] !== step
      ) {
        continue;
      }

      for (const document of phaseData.documents) {
        const originalId = MedicalCaseDocumentResolver.resolveOriginalId(document);
        const state = document.activeVersion?.state;
        if (!originalId || state == null) {
          continue;
        }
        result.set(originalId, state);
      }
    }

    return result;
  }

  static getDocumentStatusesForPhase(
    phases: MedicalCasePhases | null | undefined,
    phase: MedicalCaseProcessingPhase
  ): Map<string, MedicalCasePhaseDocumentState> {
    if (phases == null) {
      return new Map();
    }

    const phaseData = phases[phase];
    if (phaseData == null) {
      return new Map();
    }

    const result = new Map<string, MedicalCasePhaseDocumentState>();
    for (const document of phaseData.documents) {
      const originalId = MedicalCaseDocumentResolver.resolveOriginalId(document);
      const state = document.activeVersion?.state;
      if (!originalId || state == null) {
        continue;
      }
      result.set(originalId, state);
    }

    return result;
  }
}
